import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import WhaleLogo from "./WhaleLogo";
import { API } from "../lib/constants";

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2 3.5-5 3.5-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5l-.14.01-3.6 2.8-.05.13C3.5 21.3 7.4 24 12 24z" />
    <path fill="#FBBC05" d="M5.3 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4l-.01-.16-3.7-2.8-.12.06C.5 8.3 0 10.1 0 12s.5 3.7 1.4 5.3l3.9-2.9z" />
    <path fill="#EA4335" d="M12 4.6c2.2 0 3.7.9 4.6 1.7l3.3-3.2C17.9 1.2 15.2 0 12 0 7.4 0 3.5 2.7 1.4 6.7l3.9 2.9c.9-2.9 3.6-5 6.7-5z" />
  </svg>
);

export default function AuthModal() {
  const { user, authOpen, closeAuth, setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await axios.post(url, body, { withCredentials: true });
      setUser(data);
      closeAuth();
      toast.success(
        mode === "login" ? "Welcome back to the pod!" : "Welcome to the pod, whale friend!"
      );
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {authOpen && !user && (
        <motion.div
          data-testid="auth-modal"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-ocean-navy/60 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeAuth}
        >
          <motion.div
            className="sticker-lg relative w-full max-w-md rounded-3xl bg-white p-8"
            initial={{ y: 40, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="auth-close"
              onClick={closeAuth}
              aria-label="Close sign in"
              className="absolute right-4 top-4 rounded-full p-2 transition-colors hover:bg-ocean-bg2"
            >
              <X className="h-5 w-5" />
            </button>

            <WhaleLogo className="mx-auto h-14 w-14" />
            <h2 className="mt-3 text-center font-display text-3xl font-semibold">
              {mode === "login" ? "Welcome back" : "Join the pod"}
            </h2>
            <p className="mt-1 text-center text-sm font-medium text-ocean-muted">
              Big heart. Bigger waves. One account for the whole ocean.
            </p>

            <button
              data-testid="auth-google-button"
              onClick={googleSignIn}
              className="btn-push sticker-sm mt-6 flex w-full items-center justify-center gap-3 rounded-2xl bg-white px-4 py-3.5 font-display text-base font-semibold"
            >
              <GoogleG />
              Continue with Google
            </button>

            <div className="my-5 flex items-center gap-3">
              <span className="h-[3px] flex-1 rounded bg-ocean-navy/10" />
              <span className="text-xs font-extrabold uppercase tracking-widest text-ocean-muted">
                or
              </span>
              <span className="h-[3px] flex-1 rounded bg-ocean-navy/10" />
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
              {mode === "register" && (
                <input
                  data-testid="auth-name-input"
                  type="text"
                  placeholder="Your pod name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="sticker-sm w-full rounded-xl bg-ocean-bg px-4 py-3 font-body text-sm font-semibold outline-none placeholder:text-ocean-muted/60 focus:bg-white"
                />
              )}
              <input
                data-testid="auth-email-input"
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sticker-sm w-full rounded-xl bg-ocean-bg px-4 py-3 font-body text-sm font-semibold outline-none placeholder:text-ocean-muted/60 focus:bg-white"
              />
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                placeholder={mode === "login" ? "Password" : "Password (min 6 characters)"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="sticker-sm w-full rounded-xl bg-ocean-bg px-4 py-3 font-body text-sm font-semibold outline-none placeholder:text-ocean-muted/60 focus:bg-white"
              />
              {error && (
                <p
                  data-testid="auth-error"
                  className="rounded-xl bg-rose-100 px-4 py-2.5 text-sm font-bold text-rose-700"
                >
                  {error}
                </p>
              )}
              <button
                data-testid="auth-submit-button"
                type="submit"
                disabled={busy}
                className="btn-push sticker w-full rounded-2xl bg-ocean-yellow px-4 py-3.5 font-display text-lg font-semibold disabled:opacity-60"
              >
                {busy ? "Swimming…" : mode === "login" ? "Log in" : "Create account"}
              </button>
            </form>

            <p className="mt-5 text-center text-sm font-semibold text-ocean-muted">
              {mode === "login" ? "New around here?" : "Already in the pod?"}{" "}
              <button
                data-testid="auth-mode-toggle"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                }}
                className="text-ocean-cyan underline underline-offset-4 hover:text-ocean-navy"
              >
                {mode === "login" ? "Create an account" : "Log in"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
